<!-- tabindex="-1" is a programmatic focus target only, so a decision can hand focus to the
     neighbouring card without adding a second tab stop per card. scroll-mt-9 keeps the sticky
     group header from covering the card that focus lands on. cursor-pointer because the card
     is a click target on a div that hover colour alone does not advertise (ADR-0007). -->
<div
  #cardRoot
  tabindex="-1"
  class="group/card flex overflow-hidden bg-project-2500 border rounded border-project-600 transition cursor-pointer hover:bg-white hover:border-brand-blue-dark-1 hover:shadow scroll-mt-9 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
  data-testid="smartLinkCard"
  (click)="onCardClick($event)"
>
  <div class="flex flex-col flex-1 min-w-0 px-2.5 py-2 gap-y-1">
    <ng-container *ngFor="let row of evidenceRows; let first = first">
      <!-- A button, not a div with a click: the queue is worked through from the keyboard, and
           this is the one action on a card that a read-only article still offers. -->
      <button
        type="button"
        class="flex flex-col w-full text-left gap-y-0.5 hover:cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
        data-testid="cardEvidence"
        [attr.aria-label]="'Show the ' + row.label + ' citation in the article'"
        (click)="onFlashEvidence(row)"
      >
        <!-- text-xxs carries no paired line-height in tailwind.config.js, so every row here pins
             leading. The greys are the dark end of the scale: #999 reads 2.9:1 at this size. -->
        <div class="flex items-baseline text-xxs leading-4 gap-x-1.5">
          <span
            class="font-semibold tracking-wide uppercase shrink-0 text-project-4400 group-hover/card:underline group-hover/card:underline-offset-2"
            data-testid="cardEvidenceField"
            ><i class="icon icon-crosshairs mr-1" aria-hidden="true"></i
            >{{ row.label }}</span
          >
          <span class="min-w-0 truncate text-project-2400" data-testid="cardEvidenceCount">{{
            row.countLabel
          }}</span>
          <!-- Once, on the first field row: it scores the mention, not each field it was
               found in. -->
          <ng-container
            *ngIf="first"
            [ngTemplateOutlet]="cardVerdict"
          ></ng-container>
        </div>
        <!-- No whitespace may creep in around <mark>: the three parts carry the article's own
             spacing and have to read back as one sentence. bg-transparent is load-bearing — the
             UA stylesheet paints <mark> yellow and tailwind's preflight does not reset it. -->
        <span
          class="pl-2 italic border-l-2 text-xs leading-4 line-clamp-2 border-project-50 text-project-2400 group-hover/card:border-project-4400"
          data-testid="cardEvidenceQuote"
          [title]="row.title"
          >“{{ row.snippet.before
          }}<mark class="bg-transparent font-semibold text-project-2000">{{
            row.snippet.match
          }}</mark
          >{{ row.snippet.after }}”</span
        >
      </button>
    </ng-container>
    <!-- The contract always carries evidence, but the verdict is not the thing to lose if a run
         ever comes back without any. -->
    <div
      class="flex items-baseline text-xxs leading-4"
      *ngIf="evidenceRows.length === 0"
      [ngTemplateOutlet]="cardVerdict"
    ></div>

    <div class="flex flex-col">
      <div
        class="flex flex-col gap-y-1 pt-1.5 mt-1.5 border-t border-project-600 first:pt-0 first:mt-0 first:border-t-0"
        *ngFor="let candidate of candidates; trackBy: trackCandidate"
        [class.opacity-75]="isDismissed(candidate)"
        data-testid="smartLinkCandidate"
        [attr.data-smart-link-id]="candidate.item.smart_link_id"
      >
        <div
          class="flex items-start px-2 py-1.5 rounded-sm gap-x-1.5 bg-project-2200"
        >
          <p
            class="flex-1 min-w-0 text-sm font-semibold text-project-2000 line-clamp-2"
            [class.line-through]="isDismissed(candidate)"
            data-testid="cardDisplayName"
            [title]="candidate.item.display_name"
          >
            {{ candidate.item.display_name }}
          </p>
          <span
            class="mt-0.5 shrink-0 text-xxs leading-4 tabular-nums text-project-2400"
            *ngIf="showRecordId"
            data-testid="cardRecordId"
            >{{ candidate.item.record_id }}</span
          >
          <!-- An anchor rather than window.open so middle-click and open-in-new-window work, and
               a new tab rather than an in-app route so the queue is never destroyed under a
               journalist who is only checking which record this is. -->
          <a
            *ngIf="candidate.recordUrl as url"
            [href]="url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center justify-center w-6 h-6 -mt-0.5 -mr-1 rounded shrink-0 text-project-4400 hover:cursor-pointer hover:text-project-2700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
            data-testid="cardRecordLink"
            [attr.aria-label]="
              'Open record for ' + candidate.item.display_name + ' in a new tab'
            "
            [title]="'Open record for ' + candidate.item.display_name"
          >
            <i class="icon icon-info-circle" aria-hidden="true"></i>
          </a>
        </div>

        <p
          class="text-xs text-project-2400"
          *ngIf="showAlternativeHint"
          data-testid="cardAmbiguityHint"
        >
          Possible alternative: {{ alternativeNames.join(", ") }}
        </p>

        <div
          class="flex flex-row flex-wrap items-center gap-x-2 gap-y-1.5"
          *ngIf="showRecordMeta(candidate)"
        >
          <span
            *ngIf="showTypeChip"
            class="px-1.5 py-0.5 bg-white border rounded-sm text-xxs leading-4 border-project-600 text-project-2400"
            data-testid="cardTypeChip"
            >{{ typeLabel(candidate) }}</span
          >
          <app-smart-link-strength
            *ngIf="isAmbiguous"
            [confidence]="candidate.item.confidence"
          ></app-smart-link-strength>
          <div
            class="flex items-center gap-x-1.5 whitespace-nowrap"
            *ngIf="isDismissed(candidate) || isLinked(candidate) || isAlreadyTagged(candidate)"
          >
            <span
              *ngIf="isDismissed(candidate)"
              class="text-xxs text-project-2400"
              data-testid="cardRejectReason"
              >{{ reasonLabel(candidate) }}</span
            >
            <span
              *ngIf="isLinked(candidate)"
              class="text-xxs text-project-2400"
              data-testid="cardLinkedLocation"
              >{{ linkedLocationLabel(candidate) }}</span
            >
            <span
              *ngIf="isAlreadyTagged(candidate)"
              class="px-1.5 py-0.5 bg-white border rounded-sm text-xxs leading-4 border-project-600 text-project-2400"
              data-testid="cardAlreadyTagged"
              >Already tagged</span
            >
          </div>
          <div class="flex items-center ml-auto gap-x-1" *ngIf="isEditMode">
            <button
              *ngIf="isLinked(candidate) && candidate.boxLabel"
              type="button"
              class="flex items-center h-6 px-2 border rounded gap-x-1 text-xxs whitespace-nowrap shrink-0 border-project-600 text-project-2400 hover:cursor-pointer hover:text-project-300 hover:border-project-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
              data-testid="unlinkBtn"
              [attr.aria-label]="
                'Unlink ' + candidate.item.display_name + ' from Related data'
              "
              [attr.aria-disabled]="isDeciding(candidate)"
              [class.opacity-50]="isDeciding(candidate)"
              (click)="onUnlink(candidate)"
            >
              <i class="icon icon-undo" aria-hidden="true"></i> Unlink
            </button>
            <button
              *ngIf="isDismissed(candidate)"
              type="button"
              class="flex items-center h-6 px-2 border rounded gap-x-1 text-xxs whitespace-nowrap shrink-0 border-project-600 text-project-2400 hover:cursor-pointer hover:text-project-300 hover:border-project-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
              data-testid="restoreBtn"
              [attr.aria-label]="'Restore link to ' + candidate.item.display_name"
              [attr.aria-disabled]="isDeciding(candidate)"
              [class.opacity-50]="isDeciding(candidate)"
              (click)="onRestore(candidate)"
            >
              <i class="icon icon-undo" aria-hidden="true"></i> Restore
            </button>
            <ng-container
              *ngIf="!isDismissed(candidate) && !isLinked(candidate)"
            >
              <!-- Reject leads in the DOM rather than being reversed with row-reverse, so the tab
                   order still matches what is on screen. Its reasons are a body-attached menu
                   rather than chips in this row, which the queue's scrollport would clip. -->
              <button
                type="button"
                class="group flex h-6 border rounded text-xxs whitespace-nowrap shrink-0 border-project-600 text-project-2400 hover:cursor-pointer hover:text-red-600 hover:border-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
                data-testid="rejectBtn"
                [attr.aria-label]="'Reject link to ' + candidate.item.display_name"
                [attr.aria-disabled]="isDeciding(candidate)"
                [class.opacity-50]="isDeciding(candidate)"
                [matMenuTriggerFor]="reasonMenu"
              >
                <span class="flex items-center px-2">Reject</span>
                <!-- group-hover rather than border-inherit: tailwind.config.js replaces the
                     default palette and never adds `inherit`, so no such utility is generated. -->
                <span
                  class="flex items-center px-1.5 border-l border-project-600 group-hover:border-red-600"
                  aria-hidden="true"
                >
                  <i class="icon icon-caret-down"></i>
                </span>
              </button>
              <mat-menu
                #reasonMenu="matMenu"
                class="bg-white border rounded shadow-lg border-project-600"
                [aria-label]="
                  'Why reject ' + candidate.item.display_name + '?'
                "
              >
                <button
                  mat-menu-item
                  type="button"
                  class="h-8 px-3 leading-8 min-h-0 text-xxs text-project-2400 hover:cursor-pointer hover:bg-project-2500"
                  *ngFor="let reason of rejectReasons"
                  [attr.data-testid]="'reason-' + reason.value"
                  (click)="chooseReason(candidate, reason.value)"
                >
                  {{ reason.label }}
                </button>
              </mat-menu>
              <button
                type="button"
                class="flex items-center h-6 px-2 border rounded gap-x-1 text-xxs font-semibold whitespace-nowrap shrink-0 text-project-4400 border-project-4500 bg-project-4600 hover:cursor-pointer hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-project-300"
                data-testid="acceptBtn"
                [attr.aria-label]="'Accept link to ' + candidate.item.display_name"
                [attr.aria-disabled]="isDeciding(candidate)"
                [class.opacity-50]="isDeciding(candidate)"
                (click)="onAccept(candidate)"
              >
                <i class="icon icon-check" aria-hidden="true"></i> Accept
              </button>
            </ng-container>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- A card holding a choice cannot score itself here, so it states how many readings it holds
     and each of them keeps its own meter on its own row. -->
<ng-template #cardVerdict>
  <app-smart-link-strength
    *ngIf="cornerCandidate as corner"
    class="ml-auto shrink-0"
    [confidence]="corner.item.confidence"
  ></app-smart-link-strength>
  <span
    *ngIf="isAmbiguous"
    class="ml-auto shrink-0 text-project-2400"
    data-testid="cardAmbiguityCount"
    >{{ candidates.length }} possible records</span
  >
</ng-template>
